import React from 'react';
import { RSInput, RSButton, RSFormLabel } from 'reactsymbols-kit';
import { observer } from 'mobx-react';
import { useInspectorChange, useController } from '../../hooks';
import { Row, Col } from '../Grid';
import EditFSMController from '../../merode/controllers/EditFSMController';
import Styles from '../../styles/colors';
import type { IFSM } from '../../stores/model/metamodel/fsm';
import type { IFSMInspector } from '../../stores/ui/fsm_inspector';
import type { IModelVersion } from '../../merode/controllers/Controller';
import type Report from '../../merode/checks/Report';
import { asRSChange, rsValue, type RSInputChange } from './rsForm';

/** Props for the buttons at the bottom of an editable inspector. */
interface InspectorButtonsProps {
  /** Are there pending changes? */
  dirty: boolean;
  /** Callback to apply pending changes. */
  onApply: () => void;
  /** Callback to discard pending changes. */
  onDiscard: () => void;
  /** Callback to delete the element. */
  onDelete: () => void;
}

/**
 * Utility component for the buttons at the bottom of the FSM inspector.
 *
 * Props:
 * - dirty: are there pending changes?
 * - onApply: callback to apply pending changes
 * - onDiscard: callback to discard pending changes
 * - onDelete: callback to delete the FSM
 */
const FSMInspectorButtons = ({ dirty, onApply, onDiscard, onDelete }: InspectorButtonsProps) => {
  if (dirty) {
    return (
      <div className="edit-fsm p-[15px]">
        <Row gutter={5}>
          <Col md={6}>
            <RSButton
              type="button"
              background={Styles.defaultGradient}
              color={Styles.darkTextColor}
              value="Discard"
              onClick={onDiscard}
            />
          </Col>
          <Col md={6}>
            <RSButton
              type="submit"
              background={Styles.blueGradient}
              value="Apply changes"
              onClick={onApply}
            />
          </Col>
        </Row>
      </div>
    );
  } else {
    return (
      <div className="remove-fsm p-[15px]">
        <RSButton
          type="button"
          background={Styles.redGradient}
          value="Remove FSM"
          onClick={onDelete}
        />
      </div>
    );
  }
};

/**
 * Utility component for the 'codegeneration' property of an FSM.
 *
 * Props:
 * - selected: is the FSM selected for codegeneration?
 * - onSelect: callback to select the FSM for codegeneration
 */
interface SelectForCodeGenerationProps {
  /** Is the FSM selected for codegeneration? */
  selected: boolean;
  /** Callback to select the FSM for codegeneration. */
  onSelect: () => void;
}

const SelectForCodeGeneration = ({ selected, onSelect }: SelectForCodeGenerationProps) => {
  return (
    <div className="fsm-codegeneration my-[15px]">
      {selected ? (
        'Selected for codegeneration'
      ) : (
        <RSButton
          background={Styles.blueGradient}
          value="Select for code generation"
          onClick={onSelect}
        />
      )}
    </div>
  );
};

/**
 * The FSM inspector.
 *
 * Props:
 * - fsm: the FSM
 * - ui: the FSMInspector store
 * - version: The ModelVersion (to pass to the controller)
 * - onReport: callback to call when a report is generated
 * - requestClose: callback to close the inspector
 * - onCopyFSM: callback to copy the FSM
 */
interface FSMInspectorProps {
  /** The FSM to inspect. */
  fsm: IFSM;
  /** The FSMInspector ui store. */
  ui: IFSMInspector;
  /** The ModelVersion (to pass to the controller). */
  version: IModelVersion;
  /** Callback to call when a report is generated. */
  onReport?: (report: Report) => void;
  /** Callback to copy the FSM. */
  onCopyFSM: (objectTypeId: string, fsmId: string) => void;
  /** Callback to close the inspector. */
  requestClose?: () => void;
}

const FSMInspector = ({ fsm, ui, version, onReport, onCopyFSM, requestClose }: FSMInspectorProps) => {
  // `useInspectorChange` reads/writes the change value by key on `fsm`; type its
  // element param as the string-record surface it indexes (the inspector edits
  // only `name`). Same node at runtime — narrowing cast only.
  const [name, onChangeName, nameDirty] = useInspectorChange<string | null>(
    ui,
    fsm as unknown as Record<string, string | null>,
    'name'
  );
  const controller = useController(() => new EditFSMController(version));
  // `checkNameInput`/`editFSM` are typed for `string`, but the pending-change
  // value is `string | null`; the controller (via `isEmpty`) and the command
  // layer both handle `null` at runtime. Cast at these boundaries — the runtime
  // value, including `null`, is unchanged.
  const nameCheck = controller.checkNameInput(name as string);
  const nameOK = nameCheck.ok || fsm.type === 'DEFAULT';

  // Function to apply the pending changes
  const onApply = async () => {
    ui.setProjectBusy(true);
    const report = await controller.editFSM(fsm.objectType.id, fsm.id, {
      name: name as string,
    });
    if (report.successful) {
      ui.discardChanges();
    }
    ui.setProjectBusy(false);
    if (onReport != null) {
      onReport(report);
    }
  };

  // Function to select the FSM for code generation
  const onSelect = async () => {
    const report = await controller.selectFSMForCodeGeneration(fsm.objectType.id, fsm.id);
    if (onReport != null) {
      onReport(report);
    }
  };

  // Function to delete the FSM
  const onDelete = async () => {
    ui.setProjectBusy(true);
    // finally: a rejected deleteFSM must never leave the app wedged behind the
    // busy modal (it did, when a dangling FSMView made findFSMById throw).
    try {
      const report = await controller.deleteFSM(fsm.objectType.id, fsm.id);
      if (requestClose != null) {
        requestClose();
      }
      if (onReport != null) {
        onReport(report);
      }
    } finally {
      ui.setProjectBusy(false);
    }
  };

  return (
    <div className="fsm-inspector flex h-full flex-col">
      <div className="fsm-content flex-1 overflow-auto px-[15px] py-5">
        <RSFormLabel>Object</RSFormLabel>
        <div>{fsm.objectType.name}</div>

        <RSInput
          label="FSM name"
          value={rsValue(name)}
          onChange={asRSChange<RSInputChange>(onChangeName)}
          className={nameDirty ? 'dirty' : ''}
          level={rsValue(nameOK ? null : 'danger')}
          text={rsValue(nameOK ? null : nameCheck.message)}
          disabled={fsm.type === 'DEFAULT'}
        />
        <SelectForCodeGeneration selected={fsm.codegeneration} onSelect={onSelect} />
        <RSButton
          background={Styles.blueGradient}
          value="Copy FSM"
          onClick={() => onCopyFSM(fsm.objectType.id, fsm.id)}
        />
      </div>
      {fsm.type !== 'DEFAULT' && (
        <FSMInspectorButtons
          dirty={ui.dirty}
          onApply={onApply}
          onDiscard={ui.discardChanges}
          onDelete={onDelete}
        />
      )}
    </div>
  );
};

// Wrap the view in observer
const ObservedFSMInspector = observer(FSMInspector);
export default ObservedFSMInspector;
